import { ArrowRight, Heart, MessageCircle, Hash, Building2, BookUser, Users, FolderOpen, LifeBuoy, Sparkles, UserPlus, Cake, Trophy, Newspaper, ArrowLeftRight } from "lucide-react";
import Avatar from "@/components/Avatar";
import { fileUrl } from "@/components/PhotoUploader";
import { SectionHead, relTime, cover } from "./HomeBlocks";

export function WasaGramBlock({ data, onOpen }) {
  const latest = data?.latest || [];
  const trends = data?.trends || [];
  return (
    <section className="border border-[#dbe4ff] rounded-lg p-4 sm:p-5" style={{ background: "#f3f6fd" }} data-testid="intranet-wasagram">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <img src="/intranet/roy.png" alt="" className="w-11 h-11 object-contain"/>
          <div>
            <h2 className="text-base md:text-lg font-bold text-[#0b1b3a]">WasaGram <span className="text-[#1d4ed8]">— La vie du réseau</span></h2>
            <p className="text-xs text-slate-500">Échangez, partagez, vibrez ensemble !</p>
          </div>
        </div>
        <button onClick={onOpen} data-testid="intranet-wasagram-all" className="text-xs font-semibold text-[#1d4ed8] hover:underline flex items-center gap-1">Voir tout <ArrowRight className="w-3.5 h-3.5"/></button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3" data-testid="intranet-wasagram-posts">
          {latest.map(p => (
            <button key={p.id} onClick={onOpen} data-testid={`wasagram-post-${p.id}`} className="w-full text-left bg-white border border-slate-200 rounded-lg p-3 hover:shadow-md transition-shadow flex gap-3">
              <Avatar user={{ name: p.user_name, avatar_url: p.avatar_url }} size={40}/>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-bold text-[#0b1b3a]">{p.user_name}</span>
                  <span className="text-[11px] text-slate-400">{relTime(p.created_at)}{p.location ? ` — ${p.location}` : ""}</span>
                </div>
                <p className="mt-1 text-sm text-slate-600 line-clamp-2">{p.content}</p>
                <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1" data-testid={`wasagram-likes-${p.id}`}><Heart className="w-3.5 h-3.5 text-rose-500"/>{p.likes || 0}</span>
                  <span className="flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5"/>{p.comments_count || 0}</span>
                </div>
              </div>
              {p.photos?.[0] && <img src={fileUrl(p.photos[0])} alt="" className="w-24 h-24 object-cover rounded-md shrink-0"/>}
            </button>
          ))}
          {latest.length === 0 && <div className="text-sm text-slate-400 text-center py-8 bg-white rounded-lg border border-slate-200">Aucune publication pour l'instant.</div>}
        </div>
        <div className="flex flex-col gap-3">
          <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="intranet-wasagram-trends">
            <div className="flex items-center gap-1.5 text-sm font-bold text-[#0b1b3a]"><Hash className="w-4 h-4 text-[#1d4ed8]"/>Tendances du réseau</div>
            <div className="mt-3 space-y-2">
              {trends.map(t => (
                <div key={t.tag} className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-[#1d4ed8] truncate">#{t.tag.replace(/^#/, "")}</span>
                  <span className="text-slate-400 shrink-0">{t.count} publication{t.count > 1 ? "s" : ""}</span>
                </div>
              ))}
              {trends.length === 0 && <div className="text-xs text-slate-400">Pas encore de tendance.</div>}
            </div>
          </div>
          <div className="relative rounded-lg p-4 flex-1 flex flex-col items-center justify-center text-center min-h-[150px]" style={{ background: "linear-gradient(160deg,#fff7ed 0%,#fce7f3 100%)" }}>
            <div className="text-sm font-bold italic text-[#0b1b3a] flex items-start gap-1 justify-center"><Sparkles className="w-4 h-4 text-orange-500 shrink-0 mt-0.5"/>« Des idées, des équipes, une même passion ! »</div>
            <img src="/intranet/roy.png" alt="Roy" className="h-24 object-contain mt-2"/>
          </div>
        </div>
      </div>
      <div className="mt-5 flex justify-center">
        <button onClick={onOpen} data-testid="intranet-open-wasagram" className="px-6 py-2.5 rounded-lg bg-[#1d4ed8] hover:bg-blue-800 text-white text-sm font-semibold flex items-center gap-2 transition-colors shadow-sm">
          Ouvrir WasaGram <ArrowRight className="w-4 h-4"/>
        </button>
      </div>
    </section>
  );
}

export function CompanyBlock({ data, go, onAsk, navigate }) {
  const cards = [
    { key: "annuaire", icon: BookUser, tone: "bg-[#e0e9ff] text-[#1d4ed8]", title: "Annuaire", sub: `${data?.collaborateurs ?? "—"} collaborateurs`, cta: "Trouvez un collègue", onClick: () => go("annuaire") },
    { key: "equipes", icon: Users, tone: "bg-[#ede9fe] text-[#6d28d9]", title: "Nos équipes", sub: `${data?.regions ?? "—"} régions · ${data?.secteurs ?? "—"} secteurs · ${data?.corners ?? "—"} corners`, cta: "Explorer l'organisation", onClick: () => navigate("/stores") },
    { key: "ressources", icon: FolderOpen, tone: "bg-[#fce7f3] text-[#be185d]", title: "Ressources utiles", sub: `Procédures · Guides · Chartes · ${data?.documents ?? 0} documents`, cta: "Accéder aux ressources", onClick: () => navigate("/documents") },
    { key: "support", icon: LifeBuoy, tone: "bg-[#dcfce7] text-[#15803d]", title: "Services support", sub: (data?.services || []).map(s => s.key).slice(0, 4).join(" · "), cta: "Faire une demande", onClick: onAsk },
  ];
  return (
    <section data-testid="intranet-company">
      <SectionHead icon={Building2} title="Mon entreprise" linkLabel="Accéder à tous les services" onLink={() => navigate("/support/rh")} testid="intranet-company-all"/>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(c => (
          <button key={c.key} onClick={c.onClick} data-testid={`company-card-${c.key}`} className="group text-left bg-white border border-slate-200 rounded-lg p-4 hover:border-[#1d4ed8] hover:shadow-md transition-[border-color,box-shadow]">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${c.tone}`}><c.icon className="w-5 h-5"/></div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-[#0b1b3a]">{c.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{c.sub}</div>
              </div>
            </div>
            <div className="mt-3 text-xs font-semibold text-[#1d4ed8] flex items-center gap-1">{c.cta} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform"/></div>
          </button>
        ))}
      </div>
    </section>
  );
}

const MV_ICON = { nouveau: [UserPlus, "bg-[#e0e9ff] text-[#1d4ed8]"], anniversaire: [Cake, "bg-[#fce7f3] text-[#be185d]"], bravo: [Trophy, "bg-[#ffedd5] text-[#c2410c]"], mobilite: [ArrowLeftRight, "bg-[#dcfce7] text-[#15803d]"], news: [Newspaper, "bg-[#ede9fe] text-[#6d28d9]"] };

export function MovementStrip({ items = [], onNews, go }) {
  return (
    <section data-testid="intranet-movement">
      <SectionHead icon={Sparkles} title="Le Groupe en mouvement" linkLabel="Voir plus" onLink={() => go("vie")} testid="intranet-movement-all"/>
      <div className="flex gap-3 overflow-x-auto pb-1 snap-x">
        {items.map(it => {
          const [Icon, tone] = MV_ICON[it.type] || MV_ICON.news;
          const img = it.image ? cover(it.image) : it.person?.avatar_url;
          return (
            <button key={it.id} onClick={() => it.news_id ? onNews(it.news_id) : go("vie")} data-testid={`movement-${it.id}`}
                    className="snap-start shrink-0 w-64 text-left bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-md overflow-hidden shrink-0 bg-slate-100 grid place-items-center">
                {img ? <img src={img} alt="" className="w-full h-full object-cover"/> :
                 it.person ? <Avatar user={it.person} size={56}/> :
                 it.people?.length ? <div className="flex -space-x-2">{it.people.slice(0, 2).map(p => <Avatar key={p.id} user={p} size={28}/>)}</div> :
                 <div className={`w-full h-full grid place-items-center ${tone}`}><Icon className="w-5 h-5"/></div>}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-[#0b1b3a] leading-snug line-clamp-2">{it.title}</div>
                <div className="text-xs text-slate-500 line-clamp-2">{it.sub}</div>
              </div>
            </button>
          );
        })}
        {items.length === 0 && <div className="text-sm text-slate-400 py-6">Rien de nouveau pour l'instant.</div>}
      </div>
    </section>
  );
}
